import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, MessageCircle } from 'lucide-react';
import { useChat } from '../../contexts/ChatContext';
import { useToast } from '../../contexts/ToastContext';

const MESSAGGIO_NON_IN_REGOLA = 'La chat è riservata agli associati in regola con la quota associativa.';

/**
 * Card «Chat» nella dashboard dell'area riservata. Legge tutto dal `ChatProvider` di
 * tutta l'area (`useChat()`, notifiche fase A 20/10/2026): nessuna richiesta sua. Il totale
 * dei non letti (archiviate e silenziate escluse) viene da `/stato` finché l'elenco non
 * arriva, poi dall'elenco che il socket tiene aggiornato. Nascosta se la chat è spenta,
 * in prova senza di me o se lo stato non si legge.
 */
const CardChatDashboard: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { stato, elencoCaricato, totaleNonLetti } = useChat();

  if (!stato) return null;
  const inRegola = stato.disponibile;
  if (!inRegola && stato.motivo !== 'non_in_regola') return null;
  const nuova = inRegola && !(stato.regole && stato.regole.accettate);
  const nonLetti = inRegola && !nuova ? (elencoCaricato ? totaleNonLetti : stato.nonLetti || 0) : 0;

  return (
    <button
      type="button"
      onClick={(e) => {
        if (inRegola) {
          navigate('/area-riservata/chat');
        } else {
          e.preventDefault();
          toast.error(MESSAGGIO_NON_IN_REGOLA);
        }
      }}
      className={`group relative rounded-xl p-5 transition-all duration-300 text-left overflow-hidden border-2 ${
        inRegola
          ? 'bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 border-emerald-200 dark:border-emerald-700 hover:shadow-xl hover:scale-105 cursor-pointer'
          : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800/40 dark:to-gray-700/40 border-gray-300 dark:border-gray-600 opacity-50 grayscale cursor-not-allowed'
      }`}
    >
      <div className="absolute top-3 right-3 z-10">
        {!inRegola ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-500/80 text-white shadow-md">
            <Lock className="w-3 h-3" /> Quota
          </span>
        ) : nuova ? (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-600 text-white shadow-md">
            Nuovo
          </span>
        ) : nonLetti > 0 ? (
          <span
            aria-label={nonLetti === 1 ? '1 messaggio non letto' : `${nonLetti} messaggi non letti`}
            className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full text-xs font-semibold bg-[#16A34A] text-white shadow-md"
          >
            {nonLetti > 99 ? '99+' : nonLetti}
          </span>
        ) : null}
      </div>
      <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-500/10 rounded-full -mr-10 -mt-10"></div>
      <div className="relative">
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 shadow-lg ${
            inRegola
              ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 group-hover:scale-110 transition-transform'
              : 'bg-gradient-to-br from-gray-400 to-gray-500'
          }`}
        >
          <MessageCircle className="w-6 h-6 text-white" />
        </div>
        <p className={`text-sm font-bold mb-1 ${inRegola ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>Chat</p>
        <p className={`text-xs ${inRegola ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>
          Scrivi ai colleghi e segui i canali UNASCA
        </p>
      </div>
    </button>
  );
};

export default CardChatDashboard;
